import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Sparkles, Loader2, CreditCard, Shield, Gift, Crown, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import GiftDialog from "@/components/GiftDialog";
import Mascot from "@/components/Mascot";
import WelcomeTeaser from "@/components/WelcomeTeaser";

const PLAN_META = {
  kynoria_lifetime: {
    name: "Acces a vie",
    tagline: "Offre de lancement",
    color: "#E6F5EF",
    highlight: "Paiement unique",
    features: [
      "Enfants illimites, chacun son profil",
      "260 conseils par age, du nourrisson a l'ado",
      "Coach IA prioritaire, disponible jour et nuit",
      "Contes personnalises illimites avec narration audio",
      "100 activites, 40 defis en famille et album souvenirs",
      "Toutes les futures nouveautes incluses, sans rien repayer",
    ],
  },
};

const BENEFITS = [
  { t: "Une réponse tout de suite", d: "Crise à 19h, refus d'aller au lit, morsure à la crèche : le coach IA répond en quelques secondes, avec des mots concrets." },
  { t: "Adapté à l'âge exact", d: "0-3 mois, 18-24 mois, 8-9 ans, 15-16 ans… chaque conseil correspond au vrai stade de développement de ton enfant." },
  { t: "Des soirées plus douces", d: "Contes personnalisés avec le prénom de ton enfant, narration audio et rituels qui apaisent le coucher." },
  { t: "Des souvenirs qui restent", d: "Défis en famille, cartes souvenirs à partager et album photo de tous vos moments partagés." },
];

const FAQ = [
  {
    q: "Et si je n'ai pas le temps de m'en servir ?",
    a: "Tout est pensé en formats de 2 à 3 minutes : un conseil du jour, une activité prête à faire, une question au coach. Aucun cours à suivre, aucune obligation.",
  },
  {
    q: "C'est vraiment un paiement unique ?",
    a: "Oui. Tu payes 47 € une seule fois et l'accès reste ouvert à vie, mises à jour et nouveautés comprises. Aucun prélèvement automatique, rien à résilier.",
  },
  {
    q: "Mes données et celles de mes enfants sont-elles protégées ?",
    a: "Les profils enfants ne contiennent que prénom, âge et avatar. Rien n'est revendu, aucune publicité, et tu peux tout supprimer à tout moment.",
  },
  {
    q: "Est-ce que ça remplace un professionnel ?",
    a: "Non, et ce n'est pas le but. Kynoria t'accompagne au quotidien et t'invite explicitement à consulter quand une situation le demande.",
  },
];

function StatusBanner({ access }) {
  if (!access) return null;

  if (access.status === "active") {
    const meta = PLAN_META[access.lookup_key];
    return (
      <div className="rounded-3xl p-5 flex items-center gap-3 !bg-[#D2EDE1]" data-testid="active-sub-banner">
        <Shield className="h-6 w-6 text-[#23375E]" />
        <div>
          <p className="font-semibold text-[#16233D]">
            {access.lifetime ? "Acces a vie debloque" : `Abonnement actif : ${meta?.name || ""}`}
          </p>
          {!access.lifetime && access.current_period_end && (
            <p className="text-sm text-[#16233D]">
              Renouvellement le {new Date(access.current_period_end).toLocaleDateString("fr-FR")}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (access.status === "trial" && access.trial_ends_at) {
    const days = Math.max(0, Math.ceil((new Date(access.trial_ends_at) - new Date()) / 86400000));
    return (
      <div className="rounded-3xl p-5 !bg-[#FFF4DC] flex items-center gap-3" data-testid="trial-banner">
        <Sparkles className="h-6 w-6 text-[#E8899F]" />
        <div>
          <p className="font-semibold text-[#16233D]">
            Essai gratuit — {days === 0 ? "dernier jour" : `${days} jour${days > 1 ? "s" : ""} restant${days > 1 ? "s" : ""}`}
          </p>
          <p className="text-sm text-[#16233D]">
            Choisis ta formule maintenant : ton accès continue sans coupure à la fin de l'essai.
          </p>
        </div>
      </div>
    );
  }

  if (access.status === "bonus" && access.bonus_ends_at) {
    const days = Math.max(0, Math.ceil((new Date(access.bonus_ends_at) - new Date()) / 86400000));
    return (
      <div className="rounded-3xl p-5 !bg-[#D2EDE1] flex items-center gap-3" data-testid="bonus-banner">
        <Gift className="h-6 w-6 text-[#16233D]" />
        <div>
          <p className="font-semibold text-[#16233D]">
            Mois offerts actifs — {days} jour{days > 1 ? "s" : ""} restant{days > 1 ? "s" : ""}
          </p>
          <p className="text-sm text-[#16233D]">Merci pour tes parrainages ! Continue à inviter pour prolonger.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl p-5 !bg-[#FBE2E9] flex items-center gap-3" data-testid="expired-banner">
      <Shield className="h-6 w-6 text-[#E8899F]" />
      <div>
        <p className="font-semibold text-[#16233D]">Kynoria est verrouillé</p>
        <p className="text-sm text-[#16233D]">Un seul paiement de 47 € et tout s'ouvre, à vie.</p>
      </div>
    </div>
  );
}

export default function SubscribePage() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [giftPlan, setGiftPlan] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data));
  }, []);


  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur au paiement");
      setBusy(null);
    }
  };


  return (
    <div className="space-y-10" data-testid="subscribe-page">
      {/* Promesse principale */}
      <section
        className="rounded-[32px] px-6 py-10 sm:px-10 sm:py-12 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #FFF4DC 0%, #FBE2E9 45%, #E6F5EF 100%)" }}
        data-testid="subscribe-hero"
      >
        <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#16233D]">
          <Sparkles className="h-3.5 w-3.5 text-[#E8899F]" /> 47 € une seule fois · accès à vie
        </span>
        <h1
          className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#16233D] max-w-3xl leading-[1.05]"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Ne reste plus seul face aux questions du quotidien.
        </h1>
        <p className="mt-5 max-w-2xl text-base md:text-lg text-[#16233D] leading-relaxed">
          260 conseils adaptés à l'âge exact de ton enfant, un coach IA qui répond à 19h comme à 3h du matin,
          des contes personnalisés racontés à voix haute et des défis qui rapprochent toute la famille.
        </p>
        <div className="absolute right-2 bottom-0 hidden md:block">
          <Mascot pose="heart" size={190} anim="float" className="mascot-pop" />
        </div>
        <div className="mt-7 flex flex-wrap items-center gap-3">
          <a
            href="#formules"
            data-testid="hero-cta"
            className="brand-btn rounded-full px-7 py-3 font-semibold inline-flex items-center gap-2 shadow-sm hover:scale-[1.02] transition-transform duration-200"
          >
            <Crown className="h-4 w-4" /> Voir les formules
          </a>
          <span className="text-sm text-[#16233D] flex items-center gap-1.5">
            <Check className="h-4 w-4 text-[#23375E]" /> Aucun abonnement, aucun prélèvement récurrent
          </span>
          <span className="text-sm text-[#16233D] flex items-center gap-1.5">
            <Check className="h-4 w-4 text-[#23375E]" /> Paiement sécurisé Stripe
          </span>
        </div>
      </section>

      <StatusBanner access={user?.access} />

      {user?.access?.status === "expired" && <WelcomeTeaser />}

      {/* Bénéfices */}
      <section className="grid md:grid-cols-2 gap-5" data-testid="subscribe-benefits">
        {BENEFITS.map((b) => (
          <article key={b.t} className="soft-card p-6">
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-2xl bg-[#D2EDE1] grid place-items-center shrink-0">
                <Check className="h-5 w-5 text-[#23375E]" />
              </span>
              <div>
                <p className="font-semibold text-[#16233D]">{b.t}</p>
                <p className="mt-1.5 text-sm text-[#44506B] leading-relaxed">{b.d}</p>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Formules */}
      <section id="formules" className="space-y-5">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Choisis ta formule 💛
            </h2>
            <p className="mt-1 text-[#77849C] text-sm">
              Un paiement unique, un accès qui ne s'arrête jamais
            </p>
          </div>
          <span className="rounded-full bg-[#FFF4DC] px-4 py-1.5 text-sm font-semibold text-[#16233D]" data-testid="launch-badge">
            Offre de lancement — 10 € de remise
          </span>
        </div>

        <div className="grid gap-6 max-w-xl mx-auto">
          {plans.map((p) => {
            const meta = PLAN_META[p.lookup_key];
            const isCurrent = user?.access?.lookup_key === p.lookup_key;
            const featured = true;
            return (
              <article
                key={p.lookup_key}
                data-testid={`plan-${p.lookup_key}`}
                className={`soft-card p-6 flex flex-col relative transition-transform duration-200 ${
                  featured ? "ring-2 ring-[#23375E]" : ""
                }`}
                style={{ background: meta?.color }}
              >
                {meta?.highlight && (
                  <span className="absolute -top-3 right-6 rounded-full bg-[#23375E] text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                    {meta.highlight}
                  </span>
                )}
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-[#16233D]">{meta?.tagline}</p>
                  <h3 className="mt-1 text-3xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {meta?.name}
                  </h3>
                  <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                    <span className="text-5xl font-bold text-[#16233D]" data-testid="price-now">
                      {Math.round(p.amount)} €
                    </span>
                    {p.compare_at && (
                      <span className="text-2xl font-semibold text-[#99A5BB] line-through" data-testid="price-before">
                        {Math.round(p.compare_at)} €
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-semibold text-[#23375E]">
                    Un seul paiement · accès à vie · aucun abonnement
                  </p>
                </div>

                <ul className="mt-5 space-y-2 text-sm text-[#16233D] flex-1">
                  {meta?.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-[#23375E] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`subscribe-${p.lookup_key}`}
                  disabled={busy || isCurrent}
                  onClick={() => subscribe(p.lookup_key)}
                  className={`mt-6 w-full rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition-all duration-200 ${
                    isCurrent
                      ? "bg-white text-[#16233D] cursor-default"
                      : "bg-[#16233D] hover:bg-[#16233D] text-white hover:scale-[1.02] shadow-sm"
                  }`}
                >
                  {busy === p.lookup_key ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Redirection...
                    </>
                  ) : isCurrent ? (
                    "Tu as déjà l'accès à vie"
                  ) : (
                    <>
                      <CreditCard className="h-4 w-4" /> Débloquer Kynoria à vie
                    </>
                  )}
                </button>
                <button
                  type="button"
                  data-testid={`gift-${p.lookup_key}`}
                  onClick={() => setGiftPlan(p.lookup_key)}
                  className="mt-2 w-full rounded-full py-2.5 font-medium border border-[#E8899F] text-[#16233D] bg-white hover:bg-[#FBE2E9] transition-colors duration-200 inline-flex items-center justify-center gap-2"
                >
                  <Gift className="h-4 w-4 text-[#E8899F]" /> Offrir
                </button>
              </article>
            );
          })}
        </div>
      </section>

      {/* Parrainage */}
      <Link
        to="/parrainage"
        data-testid="referral-cta"
        className="soft-card p-5 flex items-center gap-4 !bg-[#FBE2E9] border-[#E8899F] hover:!bg-[#F8CDD8] transition-colors duration-200"
      >
        <div className="h-11 w-11 rounded-2xl bg-white/70 grid place-items-center shrink-0">
          <Gift className="h-6 w-6 text-[#E8899F]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#16233D]">Invite un ami, débloque la voix</p>
          <p className="text-sm text-[#16233D]">
            Partage ton code parrain — ton ami reçoit 3 jours d'essai, et tu débloques 5 contes racontés à voix haute dès qu'il achète.
          </p>
        </div>
        <span className="text-[#16233D] font-semibold text-sm hidden sm:block">Voir →</span>
      </Link>

      {/* Objections */}
      <section className="space-y-3" data-testid="subscribe-faq">
        <h2 className="text-2xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Tu hésites encore ?
        </h2>
        {FAQ.map((f, i) => (
          <div key={f.q} className="soft-card overflow-hidden p-0">
            <button
              data-testid={`faq-${i}`}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
            >
              <span className="font-semibold text-[#16233D]">{f.q}</span>
              {openFaq === i ? (
                <X className="h-4 w-4 text-[#99A5BB] shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-[#99A5BB] shrink-0" />
              )}
            </button>
            {openFaq === i && (
              <p className="px-5 pb-5 text-[#44506B] leading-relaxed">{f.a}</p>
            )}
          </div>
        ))}
      </section>

      <div className="soft-card p-5 flex items-center gap-4 flex-wrap justify-center text-[#77849C] text-sm">
        <span className="font-semibold text-[#16233D] flex items-center gap-2">
          <Shield className="h-4 w-4 text-[#23375E]" /> Paiement sécurisé, aucune donnée bancaire stockée par Kynoria :
        </span>
        <span translate="no" className="notranslate rounded-full bg-black text-white px-3 py-1 text-xs font-bold">Apple Pay</span>
        <span translate="no" className="notranslate rounded-full bg-white border border-[#E3E8F1] px-3 py-1 text-xs font-bold text-[#1A1F71]">VISA</span>
        <span translate="no" className="notranslate rounded-full bg-white border border-[#E3E8F1] px-3 py-1 text-xs font-bold text-[#EB001B]">Mastercard</span>
        <span translate="no" className="notranslate rounded-full bg-white border border-[#E3E8F1] px-3 py-1 text-xs font-bold text-[#16233D]">CB</span>
      </div>

      <GiftDialog open={!!giftPlan} onOpenChange={(v) => !v && setGiftPlan(null)} lookupKey={giftPlan} />
    </div>
  );
}
